import { Injectable } from "@angular/core";
import { PatientBase } from "@backend/graph/patients/patient-base";
import { BasePatientsService } from "projects/core/app/services/abstracts/patients.service";
import { Constants } from "projects/patient-app/constants";

export const PATIENT_UNAUTHENTICATED_QUERY = () => `
  {
    patient {
      acquisition_source_id
      first_name
      id
      payment_plan_id
      portal_url_full
      prevent_appointment_booking
      registered_portal_user
      site_id
      preferred_phone_number
      age
      date_of_birth
      dentist_recall_date
      hygienist_recall_date
      is_nhs
      nhs_country
    }
  }`;

export const PATIENT_QUERY = `
  {
    patient {
      acquisition_source_id
      account_id
      active
      bad_debtor
      date_of_birth
      dentist {
        full_name
      }
      dentist_id
      dentist_recall_date
      dentist_recall_interval
      email_address
      emergency_contact_name
      emergency_contact_phone
      emergency_contact_phone_country
      emergency_contact_phone_normalized
      emergency_contact_relationship
      gp_practice_name
      gp_practice_address_line_1
      gp_practice_address_line_2
      gp_practice_address_line_3
      gp_practice_address_line_4
      gp_practice_address_line_5
      gp_practice_postcode
      gp_practice_phone
      ethnicity
      first_name
      gender
      hygienist {
        full_name
      }
      hygienist_id
      hygienist_recall_date
      hygienist_recall_interval
      id
      last_name
      marketing
      mobile_phone_country
      mobile_phone_normalized
      mobile_phone
      home_phone_country
      home_phone
      work_phone_country
      work_phone
      nhs_country
      nhs_number
      ni_number
      payment_plan_id
      preferred_phone_number
      prevent_appointment_booking
      registered_portal_user
      site_id
      use_email
      use_sms

      address_line_1
      address_line_2
      address_line_3
      town
      county
      postcode

      first_appointment_date
      first_exam_date
      last_appointment_date
      last_cancelled_appointment_date
      last_exam_date
      last_fta_appointment_date
      last_scale_and_polish_date
      next_appointment_date
      next_exam_date
      next_scale_and_polish_date
      nhs_exemption_code
      can_book_together_dental
      can_book_together_hygiene

      age
      cancellation_policy {
        can_cancel_online
        can_cancel_online_up_to_hours
        charge_if_less_than_hours
        charge_per_minute
      }
      is_nhs
      portal_url
      portal_url_full
      portal_url_is_old_world
    }
  }
`;

export enum E_PatientInfoStatus {
  SUCCESS = "SUCCESS",
  NO_CHANGE = "NO_CHANGE",
  SAVING = "SAVING",
  ERROR = "ERROR",
}

@Injectable({
  providedIn: "root",
})
export class PatientsService extends BasePatientsService {
  protected _getPatientQuery(): string {
    const is_patient_unauthenticated = this._jwtService.isPatientUnauthenticated();
    // We only set this value if the patient has authenticated via a recall appointment link (see recall-appointment-link-handler.component.ts)
    const is_recall_flow = this._cacheService.get(Constants.RECALL_APPT_LINK_DOB_STORAGE_KEY);
    if (is_recall_flow) return PATIENT_QUERY;
    return is_patient_unauthenticated ? PATIENT_UNAUTHENTICATED_QUERY() : PATIENT_QUERY;
  }

  protected _setPatientInfo(patient: PatientBase): void {
    // This value will be set if the patient has authenticated via a recall appointment link
    const recallApptDateOfBirth = this._cacheService.get(Constants.RECALL_APPT_LINK_DOB_STORAGE_KEY);
    if (recallApptDateOfBirth && this.patientInfo) {
      this.patientInfo.date_of_birth = recallApptDateOfBirth;
      this._cacheService.delete(Constants.RECALL_APPT_LINK_DOB_STORAGE_KEY);
    }
    super._setPatientInfo(patient);
  }
}
